import {
  analyserGraphe,
  type Bloc,
  type GrapheParcours,
} from '@funo/grammaire'

type Position = GrapheParcours['positions'][string]

function vide(graphe: GrapheParcours): boolean {
  return (
    graphe.entree === null &&
    graphe.liens.length === 0 &&
    Object.keys(graphe.positions).length === 0
  )
}

/** Place uniquement le nouveau bloc ; les positions existantes appartiennent à l’utilisateur. */
function positionNouvelle(
  graphe: GrapheParcours,
  precedent?: string,
  suivant?: string,
): Position {
  const avant = precedent ? graphe.positions[precedent] : undefined
  const apres = suivant ? graphe.positions[suivant] : undefined
  const proposition =
    avant &&
    apres &&
    (Math.abs(avant.x - apres.x) >= 640 || Math.abs(avant.y - apres.y) >= 440)
      ? { x: (avant.x + apres.x) / 2, y: (avant.y + apres.y) / 2 }
      : avant
        ? { x: avant.x + 320, y: avant.y }
        : apres
          ? { x: apres.x - 320, y: apres.y }
          : { x: 80, y: 100 }
  const position = {
    x: Math.max(-999680, Math.min(999680, proposition.x)),
    y: Math.max(-999780, Math.min(999780, proposition.y)),
  }
  const occupees = Object.values(graphe.positions)
  while (
    occupees.some(
      (p) =>
        Math.abs(p.x - position.x) < 280 && Math.abs(p.y - position.y) < 180,
    )
  ) {
    position.y = position.y + 220 > 999780 ? -999780 : position.y + 220
  }
  return position
}

/**
 * Réconcilie les changements de formulaires et ajouts automatiques avec le
 * canevas. Une configuration modifiée ne réécrit jamais l’ordre dessiné.
 * Un graphe incomplet reste incomplet jusqu’à une connexion explicite.
 */
export function ajusterGraphe(
  graphe: GrapheParcours,
  avant: Bloc[],
  apres: Bloc[],
): GrapheParcours {
  const resultat = structuredClone(graphe)
  const anciennes = new Set(avant.map((b) => b.cle))
  const nouvelles = new Set(apres.map((b) => b.cle))
  const supprimees = [...anciennes].filter((cle) => !nouvelles.has(cle))
  const ajoutees = apres.filter((b) => !anciennes.has(b.cle))
  if (!supprimees.length && !ajoutees.length) return resultat

  const etaitCoherent =
    analyserGraphe(avant, graphe).complet || (!avant.length && vide(graphe))

  for (const cle of supprimees) {
    const entrants = resultat.liens.filter((l) => l.vers === cle)
    const sortants = resultat.liens.filter((l) => l.de === cle)
    const precedent = entrants.length === 1 ? entrants[0]!.de : undefined
    const suivant = sortants.length === 1 ? sortants[0]!.vers : undefined
    resultat.liens = resultat.liens.filter(
      (l) => l.de !== cle && l.vers !== cle,
    )
    delete resultat.positions[cle]
    if (resultat.entree === cle)
      resultat.entree = suivant && suivant !== cle ? suivant : null
    if (
      precedent &&
      suivant &&
      precedent !== cle &&
      suivant !== cle &&
      precedent !== suivant &&
      anciennes.has(precedent) &&
      anciennes.has(suivant) &&
      !resultat.liens.some((l) => l.de === precedent && l.vers === suivant)
    )
      resultat.liens.push({ de: precedent, vers: suivant })
  }

  const presents = new Set(
    avant.filter((b) => nouvelles.has(b.cle)).map((b) => b.cle),
  )
  let raccorder =
    etaitCoherent &&
    ((!presents.size && vide(resultat)) ||
      analyserGraphe(
        avant.filter((b) => presents.has(b.cle)),
        resultat,
      ).complet)
  for (const bloc of ajoutees) {
    const rang = apres.findIndex((b) => b.cle === bloc.cle)
    const precedent = apres
      .slice(0, rang)
      .reverse()
      .find((b) => presents.has(b.cle))?.cle
    const suivant = apres.slice(rang + 1).find((b) => presents.has(b.cle))?.cle
    // Une position déjà dessinée pour cette clé demeure intacte, même si le
    // canevas signalait jusque-là une référence inconnue.
    if (!resultat.positions[bloc.cle])
      resultat.positions[bloc.cle] = positionNouvelle(
        resultat,
        precedent,
        suivant,
      )

    let insere = false
    if (
      raccorder &&
      !presents.size &&
      resultat.entree === null &&
      !resultat.liens.length
    ) {
      resultat.entree = bloc.cle
      insere = true
    } else if (raccorder && precedent && suivant) {
      const liaison = resultat.liens.findIndex(
        (l) => l.de === precedent && l.vers === suivant,
      )
      if (liaison >= 0) {
        resultat.liens.splice(
          liaison,
          1,
          { de: precedent, vers: bloc.cle },
          { de: bloc.cle, vers: suivant },
        )
        insere = true
      }
    } else if (raccorder && !precedent && suivant === resultat.entree) {
      resultat.liens.unshift({ de: bloc.cle, vers: suivant! })
      resultat.entree = bloc.cle
      insere = true
    } else if (
      raccorder &&
      precedent &&
      !suivant &&
      !resultat.liens.some((l) => l.de === precedent)
    ) {
      resultat.liens.push({ de: precedent, vers: bloc.cle })
      insere = true
    }
    presents.add(bloc.cle)
    // Une insertion ambiguë ne devient pas le point d’ancrage d’autres étapes.
    if (!insere) raccorder = false
  }
  return resultat
}
